import { buildLocalizedPath } from '@/modules/languages/lib/localizedPath'
import { getServerSideURL } from '@/utilities/getURL'

/**
 * Caminho canónico do perfil do autor — SEM barra final (a forma com barra responde 308).
 *
 * SEO-3 (auditoria SEO 14/09) — S16: aceita também o id em TEXTO (`populatedAuthors[].id` é string)
 * para os links VISÍVEIS (byline, cartão do autor, cards, listagens) saírem do MESMO construtor do
 * canonical/sitemap em vez de montarem `/posts/user/${id}/` à mão. Com número a saída é idêntica.
 */
export function buildAuthorProfilePath(userId: number | string): string {
  return `/posts/user/${userId}`
}

export function buildAuthorProfilePagePath(userId: number, page: number): string {
  if (page <= 1) return buildAuthorProfilePath(userId)
  return `/posts/user/${userId}/page/${page}`
}

/**
 * SEO-2 (auditoria SEO 14/09): URL ABSOLUTA e LOCALIZADA do perfil do autor, para os sinais de autoria
 * lidos por robôs (`author.url` do JSON-LD do artigo e `<link rel="author">` do `<head>`).
 *
 * Antes cada lugar montava a sua: o JSON-LD saía com barra final (`/posts/user/1/`, que responde 308
 * para `/posts/user/1`) e o `rel=author` saía RELATIVO e sem prefixo de idioma — a página `/en/…`
 * apontava o perfil português. Aqui o caminho vem do MESMO construtor do canonical/sitemap do perfil
 * (`buildAuthorProfilePath`, sem barra) e o prefixo do MESMO `buildLocalizedPath` do hreflang: no idioma
 * padrão (pt) o caminho sai sem prefixo.
 *
 * Id não numérico (autor sem conta interna) → `null`: sem perfil público não há URL a anunciar.
 */
export function buildAuthorProfileUrl(
  internalId: string | number | null | undefined,
  locale: string,
  defaultLocale?: string,
): string | null {
  const id = internalId == null ? '' : String(internalId).trim()
  if (!/^[0-9]+$/.test(id)) return null
  const base = getServerSideURL().replace(/\/$/, '')
  return `${base}${buildLocalizedPath(buildAuthorProfilePath(Number(id)), locale, defaultLocale)}`
}

/**
 * T-SEO-29 (c) (auditoria SEO 18/09, D-S2): `@id` da ENTIDADE do autor no JSON-LD — a MESMA régua no
 * artigo (`PostArticleJsonLd.tsx`, `author`/`editor`) e no perfil (`AuthorPublicTemplate.tsx`,
 * `mainEntity` do `ProfilePage`), para o Google ligar os dois nós.
 *
 * - pessoa (padrão) → `<perfil>#person`, a forma de sempre (T-SEO-29 a);
 * - perfil de redação (`publicProfile.isNewsroom`) → `<perfil>#organization`: a equipa sai
 *   `Organization`, e um `@id` `#person` num nó `Organization` seria a mesma imprecisão com outro nome.
 *
 * `profileUrl` = URL absoluta do perfil (página 1) no idioma da página; a barra final é cortada. Só
 * `true` liga a redação — `null`/`undefined` (perfil sem o campo) = pessoa.
 */
export function buildAuthorEntityJsonLdId(profileUrl: string, isNewsroom?: boolean | null): string {
  const perfil = profileUrl.endsWith('/') ? profileUrl.slice(0, -1) : profileUrl
  return `${perfil}${isNewsroom === true ? '#organization' : '#person'}`
}
